import { useQuery } from '@tanstack/react-query';
import {
  type AwolFilter,
  type QueryAdditionalOptions,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { reportsKeys } from './keys';

const attendanceAwolReports = graphql(/* GraphQL */ `
  query attendance_awolReport($filter: AwolFilter!) {
    attendance_awolReport(filter: $filter) {
      date
      partyId
      student {
        partyId
        person {
          partyId
          firstName
          lastName
          avatarUrl
          type
        }
        extensions {
          priority
          aen
          medical
        }
        yearGroups {
          name
        }
      }
      classGroupId
      classGroup {
        partyId
        name
      }
      absentEvent {
        eventId
        startTime
        endTime
        name
        description
        rooms {
          name
        }
      }
      absentSubjectGroup {
        partyId
        subjectGroupType
        name
        subjects {
          colour
        }
      }
      absentUpdatedBy {
        partyId
        firstName
        lastName
        type
        avatarUrl
        title {
          id
          name
          nameTextId
        }
      }
      absentCreatedBy {
        partyId
        firstName
        lastName
        type
        avatarUrl
        title {
          id
          name
          nameTextId
        }
      }
      presentEvent {
        eventId
        allDayEvent
        startTime
        endTime
        name
        rooms {
          name
        }
      }
      presentSubjectGroup {
        partyId
        subjectGroupType
        name
        subjects {
          colour
        }
      }
      presentUpdatedBy {
        partyId
        firstName
        lastName
        type
        avatarUrl
        title {
          id
          name
          nameTextId
        }
      }
      presentCreatedBy {
        partyId
        firstName
        lastName
        type
        avatarUrl
        title {
          id
          name
          nameTextId
        }
      }
    }
  }
`);

const attendanceAwolReportsQuery = (filter: AwolFilter) => ({
  queryKey: reportsKeys.awolReport(filter),
  staleTime: 0,
  queryFn: async () => gqlClient.request(attendanceAwolReports, { filter }),
});

export function getAwolReportsQuery(filter: AwolFilter) {
  return queryClient.fetchQuery(attendanceAwolReportsQuery(filter));
}

export function useAttendanceAwolReports(
  filter: AwolFilter,
  additionalOptions?: Omit<QueryAdditionalOptions, 'academicNamespaceId'>,
) {
  return useQuery({
    ...attendanceAwolReportsQuery(filter),
    ...additionalOptions,
    select: ({ attendance_awolReport }) => attendance_awolReport,
  });
}

export type ReturnTypeFromUseAttendanceAwolReports = UseQueryReturnType<
  typeof useAttendanceAwolReports
>[number];
